/**
 * Device status logic — pure function shared by the Devices page list
 * cards and the detail panel.
 *
 *   online   : last reading < 5 minutes ago
 *   stale    : last reading 5–60 minutes ago
 *   offline  : last reading > 60 minutes ago
 *   unknown  : no readings at all
 */
export function deriveDeviceStatus(lastSeenIso, now = Date.now()) {
  if (!lastSeenIso) return { id: "unknown", label: "Unknown", accent: "#71717A" };
  const last = new Date(lastSeenIso).getTime();
  if (Number.isNaN(last)) return { id: "unknown", label: "Unknown", accent: "#71717A" };
  const minutes = (now - last) / 60000;
  if (minutes < 5) return { id: "online", label: "Online", accent: "#10B981" };
  if (minutes < 60) return { id: "stale", label: "Stale", accent: "#F59E0B" };
  return { id: "offline", label: "Offline", accent: "#EF4444" };
}

const BATTERY_GROUP_EXTERNAL_ID = "homewizard:p1:battery_group";

/**
 * Identify a HomeWizard Battery Group device. Production devices may have
 * been registered before the v2 provider distinction was introduced, so the
 * stored `provider` field can still read `"homewizard"`. We match defensively
 * on any of the three signals so the bespoke detail panel renders regardless
 * of which one the production row carries:
 *
 *   provider    === "homewizard_v2"
 *   domain      === "battery"
 *   external_id === "homewizard:p1:battery_group"
 */
export function isBatteryGroupDevice(device) {
  if (!device) return false;
  if (device.provider === "homewizard_v2") return true;
  if (device.domain === "battery") return true;
  if (device.external_id === BATTERY_GROUP_EXTERNAL_ID) return true;
  return false;
}

const PROVIDER_LABELS = {
  homewizard: "HomeWizard",
  homewizard_v2: "HomeWizard v2",
  wunderground: "Wunderground",
  fusionsolar: "FusionSolar",
  open_meteo: "Open-Meteo",
  manual: "Manual",
  csv: "CSV import",
  bridge: "Local bridge",
};

/** Pretty-print a provider id. Falls back to the raw value when unknown. */
export function providerLabel(provider) {
  if (!provider) return "—";
  return PROVIDER_LABELS[provider] || provider;
}

/**
 * Resolve the data-source ribbon shown on each card:
 *   - homewizard provider + bridge sync run history → "Local bridge"
 *   - manual / csv → "Manual" / "CSV import"
 *   - cloud APIs (wunderground/open_meteo) → "Cloud API"
 */
export function deviceSource(provider) {
  if (!provider) return "Unknown";
  if (provider === "homewizard" || provider === "homewizard_v2" || provider === "bridge") return "Local bridge";
  if (provider === "manual") return "Manual";
  if (provider === "csv") return "CSV import";
  if (provider === "wunderground" || provider === "open_meteo" || provider === "fusionsolar") {
    return "Cloud API";
  }
  return providerLabel(provider);
}
